// Admin Follower Growth — daily X follower counts (from the onSchedule job
// loops/accountStats.js) plotted over time and lined up against the tweets +
// engagement that drove them. Data comes from
// functions/lib/content_loop/accountGrowthReport.js via
// src/services/accountGrowth.js.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  BarController,
  LineController,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Chart as MixedChart } from 'react-chartjs-2';
import {
  Users,
  TrendingUp,
  TrendingDown,
  CalendarDays,
  Award,
  RefreshCw,
  AlertCircle,
  ExternalLink,
} from 'lucide-react';
import { fetchAccountGrowth } from '@/services/accountGrowth';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  BarController,
  LineController,
  Tooltip,
  Legend,
  Filler
);

const fmtInt = (n) => Number(n ?? 0).toLocaleString();
const fmtDelta = (n) => (n > 0 ? `+${fmtInt(n)}` : fmtInt(n));
const shortDate = (d) => (d ? `${d.slice(5, 7)}/${d.slice(8, 10)}` : '');

const PERSONA_LABELS = { main: 'Main', mongol: 'Mongol', coderjs: 'CoderJS' };

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm p-5 flex items-center gap-4">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${color}`}>
        <Icon size={20} className="text-white" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500 truncate">{label}</p>
        <p className="text-xl font-bold text-gray-800">{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function Panel({ title, children, className = '' }) {
  return (
    <div className={`bg-white rounded-2xl shadow-sm p-6 ${className}`}>
      {title && <h2 className="text-sm font-semibold text-gray-700 mb-4">{title}</h2>}
      {children}
    </div>
  );
}

export default function AdminGrowth() {
  const [report, setReport] = useState(null);
  const [persona, setPersona] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetchAccountGrowth();
      setReport(r);
      setPersona((prev) => prev ?? r.personas?.[0] ?? null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const series = report && persona ? report.series?.[persona] ?? [] : [];

  // Posts for this persona, bucketed by day → engagement + titles.
  const postsByDay = useMemo(() => {
    const map = {};
    for (const p of report?.posts ?? []) {
      if (persona && p.persona !== persona) continue;
      (map[p.date] ??= []).push(p);
    }
    return map;
  }, [report, persona]);

  const dayImpressions = (date) =>
    (postsByDay[date] ?? []).reduce((sum, p) => sum + (p.impressions ?? 0), 0);

  // ----- summary stats ------------------------------------------------------
  const summary = useMemo(() => {
    if (!series.length) return null;
    const first = series[0];
    const last = series[series.length - 1];
    const withDelta = series.filter((s) => typeof s.followersDelta === 'number');
    const best = withDelta.reduce(
      (b, s) => (s.followersDelta > (b?.followersDelta ?? -Infinity) ? s : b),
      null
    );
    const net = last.followersCount - first.followersCount;
    return { current: last.followersCount, net, best, days: series.length };
  }, [series]);

  // ----- charts -------------------------------------------------------------
  const labels = series.map((s) => shortDate(s.date));

  const followersData = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'Followers',
          data: series.map((s) => s.followersCount),
          borderColor: '#1E3A8A',
          backgroundColor: 'rgba(30, 58, 138, 0.08)',
          fill: true,
          tension: 0.35,
          // Highlight days that had a post — the visual link to "why".
          pointRadius: series.map((s) => (postsByDay[s.date]?.length ? 6 : 2)),
          pointBackgroundColor: series.map((s) =>
            postsByDay[s.date]?.length ? '#F59E0B' : '#1E3A8A'
          ),
          pointBorderColor: series.map((s) =>
            postsByDay[s.date]?.length ? '#B45309' : '#1E3A8A'
          ),
        },
      ],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [series, postsByDay]
  );

  // Daily follower change (bars) overlaid with post impressions that day
  // (line, right axis) — the correlation view.
  const correlationData = useMemo(
    () => ({
      labels,
      datasets: [
        {
          type: 'bar',
          label: 'Follower change',
          yAxisID: 'y',
          data: series.map((s) => s.followersDelta ?? 0),
          backgroundColor: series.map((s) =>
            (s.followersDelta ?? 0) >= 0 ? 'rgba(16, 185, 129, 0.75)' : 'rgba(239, 68, 68, 0.75)'
          ),
          borderRadius: 4,
        },
        {
          type: 'line',
          label: 'Post impressions',
          yAxisID: 'y1',
          data: series.map((s) => dayImpressions(s.date)),
          borderColor: '#7C3AED',
          backgroundColor: 'transparent',
          tension: 0.3,
          pointRadius: series.map((s) => (postsByDay[s.date]?.length ? 4 : 0)),
          spanGaps: true,
        },
      ],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [series, postsByDay]
  );

  // ----- "what drove the biggest days" table --------------------------------
  const topDays = useMemo(
    () =>
      series
        .filter((s) => typeof s.followersDelta === 'number')
        .slice()
        .sort((a, b) => b.followersDelta - a.followersDelta)
        .slice(0, 8)
        .map((s) => ({ ...s, posts: postsByDay[s.date] ?? [] })),
    [series, postsByDay]
  );

  const hasData = report && report.personas?.length && series.length;

  return (
    <div className="p-8">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 mb-1">Follower Growth</h1>
          <p className="text-gray-500 text-sm">
            Daily X follower counts vs. the tweets that drove them
          </p>
        </div>
        <div className="flex items-center gap-2">
          {report?.personas?.length > 1 && (
            <div className="bg-white rounded-xl shadow-sm p-1 flex">
              {report.personas.map((p) => (
                <button
                  key={p}
                  onClick={() => setPersona(p)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    persona === p ? 'bg-[#1E3A8A] text-white' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {PERSONA_LABELS[p] ?? p}
                </button>
              ))}
            </div>
          )}
          <button
            onClick={load}
            disabled={loading}
            className="p-2.5 bg-white rounded-xl shadow-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50"
            aria-label="Refresh"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-5 mb-8 flex items-start gap-3">
          <AlertCircle size={20} className="text-red-500 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-red-700">Could not load growth data</p>
            <p className="text-sm text-red-600 mt-1">{error}</p>
          </div>
        </div>
      )}

      {loading && !report && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-sm p-5 h-24 animate-pulse" />
          ))}
        </div>
      )}

      {report && !loading && !hasData && !error && (
        <div className="bg-white rounded-2xl shadow-sm flex flex-col items-center justify-center py-20 text-gray-400 gap-2">
          <p>No follower snapshots yet.</p>
          <p className="text-xs">
            The daily job records one row per account per day. Data appears after its first run.
          </p>
        </div>
      )}

      {hasData && summary && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 mb-8">
            <StatCard
              icon={Users}
              label="Current followers"
              value={fmtInt(summary.current)}
              color="bg-[#1E3A8A]"
            />
            <StatCard
              icon={summary.net >= 0 ? TrendingUp : TrendingDown}
              label="Net change (tracked)"
              value={fmtDelta(summary.net)}
              sub={`over ${summary.days} day${summary.days === 1 ? '' : 's'}`}
              color={summary.net >= 0 ? 'bg-[#10B981]' : 'bg-[#EF4444]'}
            />
            <StatCard
              icon={Award}
              label="Best single day"
              value={summary.best ? fmtDelta(summary.best.followersDelta) : '—'}
              sub={summary.best ? shortDate(summary.best.date) : 'needs 2+ days'}
              color="bg-[#F59E0B]"
            />
            <StatCard
              icon={CalendarDays}
              label="Avg. / day"
              value={
                summary.days > 1 ? fmtDelta(Math.round(summary.net / (summary.days - 1))) : '—'
              }
              color="bg-[#7C3AED]"
            />
          </div>

          <Panel title="Followers over time" className="mb-8">
            <p className="text-xs text-gray-400 -mt-2 mb-3">
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#F59E0B] align-middle mr-1" />
              amber points mark days a tweet went out
            </p>
            <div className="h-72">
              <Line
                data={followersData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: { y: { beginAtZero: false } },
                }}
              />
            </div>
          </Panel>

          <Panel title="Daily change vs. posting activity" className="mb-8">
            <div className="h-72">
              <MixedChart
                type="bar"
                data={correlationData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { position: 'bottom', labels: { boxWidth: 12 } } },
                  scales: {
                    y: {
                      position: 'left',
                      title: { display: true, text: 'Followers ±', font: { size: 10 } },
                    },
                    y1: {
                      position: 'right',
                      beginAtZero: true,
                      grid: { drawOnChartArea: false },
                      title: { display: true, text: 'Impressions', font: { size: 10 } },
                    },
                  },
                }}
              />
            </div>
          </Panel>

          <Panel title="What drove the biggest days">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-400 border-b">
                    <th className="pb-2 font-medium">Day</th>
                    <th className="pb-2 font-medium text-right">Followers ±</th>
                    <th className="pb-2 font-medium">Tweets that day</th>
                    <th className="pb-2 font-medium text-right">Impressions</th>
                    <th className="pb-2 font-medium text-right">Likes</th>
                  </tr>
                </thead>
                <tbody>
                  {topDays.map((d) => {
                    const imp = d.posts.reduce((s, p) => s + (p.impressions ?? 0), 0);
                    const likes = d.posts.reduce((s, p) => s + (p.likes ?? 0), 0);
                    return (
                      <tr key={d.date} className="border-b border-gray-50 last:border-0 align-top">
                        <td className="py-2 text-gray-700 whitespace-nowrap">{d.date}</td>
                        <td
                          className={`py-2 text-right font-semibold whitespace-nowrap ${
                            d.followersDelta >= 0 ? 'text-emerald-600' : 'text-red-500'
                          }`}
                        >
                          {fmtDelta(d.followersDelta)}
                        </td>
                        <td className="py-2 pr-4">
                          {d.posts.length === 0 ? (
                            <span className="text-gray-400">— no tweet logged</span>
                          ) : (
                            <ul className="space-y-1">
                              {d.posts.map((p) => (
                                <li key={p.id} className="text-gray-600">
                                  {p.permalink ? (
                                    <a
                                      href={p.permalink}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="inline-flex items-center gap-1 hover:text-[#1E3A8A]"
                                    >
                                      {p.title || 'tweet'}
                                      <ExternalLink size={11} className="shrink-0" />
                                    </a>
                                  ) : (
                                    p.title || 'tweet'
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                        <td className="py-2 text-right text-gray-500 whitespace-nowrap">
                          {fmtInt(imp)}
                        </td>
                        <td className="py-2 text-right text-gray-500 whitespace-nowrap">
                          {fmtInt(likes)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-400 mt-4">
              Days are ranked by follower change. A big jump next to a high-impression tweet is your
              signal for what content grows the account.
            </p>
          </Panel>
        </>
      )}
    </div>
  );
}
